import { WaypointActionType, WaypointType } from '@caresend/types';
import { nullishFilter, getValidatedShipment, getUnknownErrorMessage, isMailInAction } from '@caresend/utils';
import { defineComponent, computed, ref, watch } from 'vue';
import __vue_component__ from '../../../../alerts/Alert.vue.js';
import __vue_component__$3 from '../../../../alerts/Pane/Pane.vue.js';
import __vue_component__$1 from '../../../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__$4 from '../../../../inputs/Radio/Radio.vue.js';
import __vue_component__$5 from '../../../../inputs/TextInput/TextInput.vue.js';
import __vue_component__$6 from '../ItineraryWaypoint/components/WaypointProcedureCard/WaypointProcedureCard.vue.js';
import __vue_component__$2 from './components/ManualWaypointActions.vue.js';
import { usePortalStore } from '../../store/portal/index.js';
import { setWaypointActionDuration, addWaypointActions, removeWaypointActionsAndMaybeWaypoint } from '../../store/portal/set/index.js';
import { useItineraryWorkflowsStore } from '../../store/portal/workflow/itinerary.js';
import TransitionExpand from '../../../../transitions/TransitionExpand/TransitionExpand.js';
import { getElementsFromIDs } from '../../../../../functions/array.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../ui/Icon.vue.js';
import '../../../../../functions/configuration/icons.js';
import '../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../functions/tracking/index.js';
import 'immutability-helper';
import { getRouter } from '../../../../../router/helpers/initRouter.js';
import '../../../../../router/routes/dev/model.js';
import '../../../../../router/routes/dev/routes.js';
import '../../../../../router/routes/dashboard/model.js';
import '../../../../../router/routes/pro/model.js';
import '../../../../../router/routes/visitsPage/model.js';
import '../../../../../router/routes/orderFlow/model.js';
import '../../../../../router/routes/orderFlow/privateHelpers.js';
import '../../../../../router/routes/orderFlow/routes.js';
import '../../../../../router/routes/schedulingFlow/model.js';
import '../../../../../router/routes/schedulingFlow/routes.js';
import '../../../../../router/routes/chartingFlow/model.js';
import '../../../../../router/routes/chartingFlow/routes.js';
import '../../../../../router/routes/portal/portalRoutes.js';
import '../../../../../router/routes/portal/model.js';
import '../../../../../router/routes/visitsPage/routes.js';
import { getStore } from '../../../../../store/helpers/initStore.js';

const controller = defineComponent({
    components: {
        Alert: __vue_component__,
        ButtonComponent: __vue_component__$1,
        ManualWaypointActions: __vue_component__$2,
        Pane: __vue_component__$3,
        Radio: __vue_component__$4,
        TextInput: __vue_component__$5,
        TransitionExpand,
        WaypointProcedureCard: __vue_component__$6,
    },
    props: {
        waypointID: {
            type: String,
            default: null,
        },
    },
    setup(props) {
        var _a, _b;
        const router = getRouter();
        const store = getStore();
        const portalStore = usePortalStore();
        const itineraryWorkflowsStore = useItineraryWorkflowsStore();
        const waypoint = computed(() => props.waypointID
            ? portalStore.waypoints.get(props.waypointID)
            : undefined);
        const waypointActions = computed(() => {
            var _a, _b;
            return props.waypointID
                ? (_b = (_a = portalStore.waypoints.get(props.waypointID)) === null || _a === void 0 ? void 0 : _a.computed.waypointActions) !== null && _b !== void 0 ? _b : []
                : [];
        });
        const draftWaypointActions = ref(waypointActions.value);
        const isWaypointInItineraryDraft = computed(() => {
            var _a, _b, _c;
            return ((_c = (_b = (_a = portalStore.itinerary.value.waypoints) === null || _a === void 0 ? void 0 : _a.some) === null || _b === void 0 ? void 0 : _b.call(_a, ({ id }) => id === props.waypointID)) !== null && _c !== void 0 ? _c : false);
        });
        const setWaypointActionDurationLocal = (waypointActionID, event) => {
            const target = event.target;
            const duration = parseInt(target.value, 10);
            setWaypointActionDuration(waypointActionID, duration);
        };
        const notesLabel = ref('These are the default notes for this los cation. You can edit, but the '
            + 'changes you make will only affect this itinerary and won’t be saved '
            + 'to the location');
        const notesText = ref((_b = (_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.placeInstructions) !== null && _b !== void 0 ? _b : '');
        const displayNotes = ref(!!notesText.value);
        const toggleDisplayNotes = () => {
            displayNotes.value = !displayNotes.value;
        };
        const closeDetailsAlert = () => {
            const { query, params } = router.currentRoute;
            if (!query.waypointID && !params.waypointID)
                return;
            itineraryWorkflowsStore.state.set.waypointID(null);
            router.push({ query: { ...query, waypointID: undefined }, params: { ...params, waypointID: '' } });
        };
        const getMailInActionFailure = (mailInAction) => {
            var _a;
            const shipmentIDs = Object.keys((_a = mailInAction.shipments) !== null && _a !== void 0 ? _a : {});
            const shipments = getElementsFromIDs(shipmentIDs, (id) => {
                var _a;
                return (_a = portalStore.draftShipments.get(id)) !== null && _a !== void 0 ? _a : store.state.supplies.shipments[id];
            }, 'shipment');
            const shipmentFailures = shipments === null || shipments === void 0 ? void 0 : shipments.map((shipment) => {
                try {
                    getValidatedShipment(shipment);
                    return null;
                }
                catch (error) {
                    return getUnknownErrorMessage(error);
                }
            }).filter(nullishFilter);
            const missingMailInBoxBarcodeFailure = !mailInAction.mailInBoxSupplyID
                ? 'Missing mail-in box type'
                : null;
            const invalidMailInActionFailure = !isMailInAction(mailInAction)
                ? 'Not a valid mail-in action'
                : null;
            const failures = [
                ...shipmentFailures,
                missingMailInBoxBarcodeFailure,
                invalidMailInActionFailure,
            ].filter(nullishFilter);
            return failures.length
                ? failures.join('\n')
                : null;
        };
        const waypointActionFailures = computed(() => {
            var _a, _b;
            return (_b = (_a = draftWaypointActions.value) === null || _a === void 0 ? void 0 : _a.map((waypointAction, index) => {
                const num = index + 1;
                let failure = null;
                if (waypointAction.type === WaypointActionType.MAIL_IN) {
                    failure = getMailInActionFailure(waypointAction);
                }
                // Only mail-in actions are validated at this time.
                if (failure)
                    return `Waypoint action ${num}: ${failure}`;
                return null;
            }).filter(nullishFilter)) !== null && _b !== void 0 ? _b : [];
        });
        const saveDisabled = computed(() => waypointActionFailures.value.length
            ? waypointActionFailures.value.join('\n')
            : false);
        const mailInWaypointActionID = ref(null);
        const setMailInWaypointActionID = ({ waypointID, waypointActionID, }) => {
            mailInWaypointActionID.value = {
                waypointID,
                waypointActionID,
            };
        };
        const commitMailInWaypointActionID = () => {
            if (mailInWaypointActionID.value) {
                const { waypointID, waypointActionID } = mailInWaypointActionID.value;
                portalStore.waypoints.setChildOf(waypointID, 'mailInWaypointActionID', waypointActionID, 'update waypoint mail in waypoint ID');
            }
        };
        const commitShipments = ref(false);
        const updateWaypointActions = ({ wpas, ...input }) => {
            var _a;
            draftWaypointActions.value = wpas;
            commitShipments.value = (_a = input.commitShipments) !== null && _a !== void 0 ? _a : false;
        };
        let deletedWaypointActions = {};
        const deleteWaypointAction = (id) => {
            deletedWaypointActions[id] = true;
        };
        const updateInfo = async () => {
            var _a;
            const waypointID = (_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.id;
            if (!waypointID)
                return;
            portalStore.waypoints.setChildOf(waypointID, 'placeInstructions', notesText.value, 'update waypoint instructions');
            addWaypointActions(draftWaypointActions.value, true);
            commitMailInWaypointActionID();
            if (Object.keys(deletedWaypointActions).length) {
                removeWaypointActionsAndMaybeWaypoint(Object.keys(deletedWaypointActions));
                deletedWaypointActions = {};
            }
            closeDetailsAlert();
        };
        const procedureIDs = computed(() => {
            var _a, _b;
            return props.waypointID
                ? Object.keys((_b = (_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.computed.procedures) !== null && _b !== void 0 ? _b : {})
                : [];
        });
        const openBooking = (bookingID) => {
            const { query } = router.currentRoute;
            router.push({ query: { ...query, bookingID } });
        };
        watch(() => waypoint.value, () => {
            draftWaypointActions.value = waypointActions.value;
        }, { immediate: true, deep: true });
        watch(() => { var _a; return (_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.placeInstructions; }, () => {
            var _a, _b;
            notesText.value = (_b = (_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.placeInstructions) !== null && _b !== void 0 ? _b : '';
        }, {
            deep: true,
        });
        return {
            WaypointType,
            closeDetailsAlert,
            displayNotes,
            draftWaypointActions,
            deleteWaypointAction,
            isWaypointInItineraryDraft,
            notesLabel,
            notesText,
            openBooking,
            procedureIDs,
            saveDisabled,
            setMailInWaypointActionID,
            setWaypointActionDuration: setWaypointActionDurationLocal,
            toggleDisplayNotes,
            updateInfo,
            updateWaypointActions,
            waypoint,
            waypointActionFailures,
            waypointActions,
        };
    },
});

export { controller };
